import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Typography,
  Button,
  Alert,
  Stack,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

export interface SyncProgress {
  syncId: string;
  listingId: string;
  listingTitle: string;
  status: 'idle' | 'starting' | 'fetching' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: {
    percentage: number;
    currentStep: string;
    totalSteps?: number;
    currentStepIndex?: number;
  };
  startedAt: string;
  completedAt?: string;
  result?: {
    bookingsFound: number;
    bookingsAdded: number;
    bookingsUpdated: number;
    bookingsRemoved: number;
    errors?: string[];
    warnings?: string[];
  };
  error?: {
    message: string;
    code?: string;
    retryable?: boolean;
  };
}

interface SyncProgressIndicatorProps {
  syncProgress: SyncProgress[];
  onCancel?: (syncId: string) => void;
  onRetry?: (listingId: string) => void;
  onDismiss?: (syncId: string) => void;
  compact?: boolean;
}

const getStatusColor = (status: SyncProgress['status']) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'cancelled':
      return 'error';
    case 'starting':
    case 'fetching':
    case 'processing':
      return 'primary';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: SyncProgress['status']) => {
  switch (status) {
    case 'completed':
      return <SuccessIcon color="success" />;
    case 'failed':
      return <ErrorIcon color="error" />;
    case 'cancelled':
      return <CancelIcon color="error" />;
    case 'starting':
    case 'fetching':
    case 'processing':
      return <SyncIcon className="rotating" />;
    default:
      return <SyncIcon />;
  }
};

const formatDuration = (startedAt: string, completedAt?: string) => {
  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const duration = Math.round((end.getTime() - start.getTime()) / 1000);
  
  if (duration < 60) {
    return `${duration}s`;
  } else if (duration < 3600) {
    return `${Math.floor(duration / 60)}m ${duration % 60}s`;
  } else {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

const SyncProgressItem: React.FC<{
  sync: SyncProgress;
  onCancel?: (syncId: string) => void;
  onRetry?: (listingId: string) => void;
  onDismiss?: (syncId: string) => void;
  compact?: boolean;
}> = ({ sync, onCancel, onRetry, onDismiss, compact = false }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [autoHideTimer, setAutoHideTimer] = useState<NodeJS.Timeout | null>(null);

  const isActive = ['starting', 'fetching', 'processing'].includes(sync.status);
  const isCompleted = sync.status === 'completed';
  const isFailed = sync.status === 'failed';
  const canCancel = isActive && onCancel;
  const canRetry = isFailed && sync.error?.retryable && onRetry;

  // Auto-hide completed syncs after 10 seconds
  useEffect(() => {
    if (isCompleted && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss(sync.syncId);
      }, 10000);
      setAutoHideTimer(timer);
      return () => clearTimeout(timer);
    }
  }, [isCompleted, onDismiss, sync.syncId]);

  useEffect(() => {
    return () => {
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
    };
  }, [autoHideTimer]);

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        {getStatusIcon(sync.status)}
        <Typography variant="body2" sx={{ flex: 1 }}>
          {sync.listingTitle}
        </Typography>
        <Chip
          label={sync.status}
          size="small"
          color={getStatusColor(sync.status) as any}
        />
        {isActive && (
          <Typography variant="caption" color="text.secondary">
            {sync.progress.percentage}%
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {getStatusIcon(sync.status)}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {sync.listingTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sync.progress.currentStep}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={sync.status}
              size="small"
              color={getStatusColor(sync.status) as any}
            />
            <Typography variant="caption" color="text.secondary">
              {formatDuration(sync.startedAt, sync.completedAt)}
            </Typography>
          </Stack>
        </Box>

        {isActive && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {sync.progress.currentStepIndex && sync.progress.totalSteps
                  ? `Step ${sync.progress.currentStepIndex} of ${sync.progress.totalSteps}`
                  : 'Processing...'
                }
              </Typography>
              <Typography variant="body2">
                {sync.progress.percentage}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={sync.progress.percentage} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {sync.result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Found {sync.result.bookingsFound} bookings: {sync.result.bookingsAdded} added, {sync.result.bookingsUpdated} updated
              {sync.result.bookingsRemoved > 0 && `, ${sync.result.bookingsRemoved} removed`}
            </Typography>
          </Alert>
        )}

        {sync.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {sync.error.message}
            </Typography>
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Box>
            {canCancel && (
              <Button
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => onCancel(sync.syncId)}
              >
                Cancel
              </Button>
            )}
            {canRetry && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => onRetry(sync.listingId)}
              >
                Retry
              </Button>
            )}
          </Box>

          <Box>
            {(sync.result?.errors?.length || sync.result?.warnings?.length) && (
              <Button
                size="small"
                startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            )}
            {onDismiss && (isCompleted || isFailed) && (
              <IconButton
                size="small"
                onClick={() => onDismiss(sync.syncId)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Stack>

        <Collapse in={showDetails}>
          <Box sx={{ mt: 2 }}>
            {sync.result?.warnings && sync.result.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Warnings:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {sync.result.warnings.map((warning, index) => (
                    <Typography key={index} component="li" variant="body2">
                      {warning}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}

            {sync.result?.errors && sync.result.errors.length > 0 && (
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>
                  Errors:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {sync.result.errors.map((error, index) => (
                    <Typography key={index} component="li" variant="body2">
                      {error}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export const SyncProgressIndicator: React.FC<SyncProgressIndicatorProps> = ({
  syncProgress,
  onCancel,
  onRetry,
  onDismiss,
  compact = false,
}) => {
  if (syncProgress.length === 0) {
    return null;
  }

  const activeSyncs = syncProgress.filter(sync => 
    ['starting', 'fetching', 'processing'].includes(sync.status)
  );
  const completedSyncs = syncProgress.filter(sync => 
    ['completed', 'failed', 'cancelled'].includes(sync.status)
  );

  return (
    <Box>
      {activeSyncs.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Syncing Calendars ({activeSyncs.length})
          </Typography>
          {activeSyncs.map(sync => (
            <SyncProgressItem
              key={sync.syncId}
              sync={sync}
              onCancel={onCancel}
              onRetry={onRetry}
              onDismiss={onDismiss}
              compact={compact}
            />
          ))}
        </Box>
      )}

      {completedSyncs.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Recent Sync Results
          </Typography>
          {completedSyncs.map(sync => (
            <SyncProgressItem
              key={sync.syncId}
              sync={sync}
              onCancel={onCancel}
              onRetry={onRetry}
              onDismiss={onDismiss}
              compact={compact}
            />
          ))}
        </Box>
      )}

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 2s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default SyncProgressIndicator;